import axios from 'axios';

// Stage 5N-2B: dedicated axios client for the Glass-i internal-admin
// surface. Completely separate from frontend/src/api/client.js so a
// tenant master/sub-user session and an internal-admin session never
// share tokens or storage.
//
// Storage keys are prefixed `glassi_internal_*` so they cannot collide
// with the tenant client's `glassi.refreshToken`. Access tokens live
// in module-scope memory only (lost on hard reload — same posture as
// the tenant client).
//
// Refresh path is `/api/internal/auth/refresh` and the token isolation
// is enforced at the JWT audience layer on the backend
// (config.jwt.internalAudience), so a tenant token cannot pass through
// here and an internal-admin token cannot pass through the tenant
// client.

const REFRESH_KEY_INTERNAL = 'glassi_internal_refresh_token';
const REFRESH_PATH_INTERNAL = '/api/internal/auth/refresh';

let _accessToken = null;
let _refreshInFlight = null;
let _onAuthFailure = null;

function getRefreshToken() {
  try {
    return window.sessionStorage.getItem(REFRESH_KEY_INTERNAL);
  } catch (_e) {
    return null;
  }
}

function setRefreshToken(token) {
  try {
    if (token) window.sessionStorage.setItem(REFRESH_KEY_INTERNAL, token);
    else window.sessionStorage.removeItem(REFRESH_KEY_INTERNAL);
  } catch (_e) {
    /* noop */
  }
}

export function getInternalAccessToken() {
  return _accessToken;
}

export function setInternalTokens({ accessToken, refreshToken }) {
  if (accessToken !== undefined) _accessToken = accessToken || null;
  if (refreshToken !== undefined) setRefreshToken(refreshToken || null);
}

export function clearInternalTokens() {
  _accessToken = null;
  setRefreshToken(null);
  // Also clear the cached admin profile so the boot path doesn't try
  // to rehydrate a stale identity.
  try {
    window.sessionStorage.removeItem('glassi_internal_admin');
  } catch (_e) {
    /* noop */
  }
}

export function hasStoredInternalRefreshToken() {
  return Boolean(getRefreshToken());
}

export function registerInternalAuthFailureHandler(fn) {
  _onAuthFailure = typeof fn === 'function' ? fn : null;
}

const internalAdminClient = axios.create({
  baseURL: '/',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

internalAdminClient.interceptors.request.use((config) => {
  if (_accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

async function performRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('no internal refresh token');

  // Bare axios call — bypass interceptors to avoid recursion if the
  // refresh itself returns 401.
  const resp = await axios.post(REFRESH_PATH_INTERNAL, { refreshToken });
  const data = resp.data && resp.data.data;
  if (!data || !data.accessToken || !data.refreshToken) {
    throw new Error('internal refresh response missing tokens');
  }
  setInternalTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken
  });
  return data.accessToken;
}

internalAdminClient.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const config = err && err.config;
    const status = err && err.response && err.response.status;

    if (status !== 401 || !config) return Promise.reject(err);

    if (config.url && config.url.includes(REFRESH_PATH_INTERNAL)) {
      clearInternalTokens();
      if (_onAuthFailure) _onAuthFailure();
      return Promise.reject(err);
    }
    if (config.__isRetry) return Promise.reject(err);

    if (!getRefreshToken()) {
      clearInternalTokens();
      if (_onAuthFailure) _onAuthFailure();
      return Promise.reject(err);
    }

    try {
      if (!_refreshInFlight) {
        _refreshInFlight = performRefresh().finally(() => {
          _refreshInFlight = null;
        });
      }
      const newAccess = await _refreshInFlight;
      const retryConfig = { ...config, __isRetry: true };
      retryConfig.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${newAccess}`
      };
      return internalAdminClient.request(retryConfig);
    } catch (refreshErr) {
      clearInternalTokens();
      if (_onAuthFailure) _onAuthFailure();
      return Promise.reject(refreshErr);
    }
  }
);

export default internalAdminClient;
